import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPaperPlane, FaArrowLeft, FaUsers, FaEllipsisV, FaImage, FaSmile, FaCheck, FaCheckDouble, FaCircle, FaPhone, FaVideo } from 'react-icons/fa';
import { useCall } from '../../contexts/CallContext';

const REACTION_EMOJIS = ['❤️', '😂', '😮', '😢', '🔥', '👍'];

const getDateLabel = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (msgDate.getTime() === today.getTime()) return 'Today';
  if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatMsgTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const shouldShowDateSeparator = (cur, prev) => {
  if (!prev) return true;
  return new Date(cur.created_at).toDateString() !== new Date(prev.created_at).toDateString();
};

const shouldShowTimeSeparator = (cur, prev) => {
  if (!prev) return true;
  return new Date(cur.created_at) - new Date(prev.created_at) > 5 * 60 * 1000;
};

const TypingIndicator = () => (
  <div className="flex items-center space-x-1 px-4 py-2 bg-white border border-gray-200 rounded-2xl w-fit" data-testid="typing-indicator">
    <div className="flex space-x-1">
      {[0, 1, 2].map(i => (
        <div key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
    <span className="text-xs text-gray-400 ml-1">typing</span>
  </div>
);

const ReactionBubbles = ({ reactions, currentUserId, onReact }) => {
  const entries = Object.entries(reactions || {}).filter(([, users]) => users.length > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="reaction-bubbles">
      {entries.map(([emoji, users]) => (
        <button
          key={emoji}
          onClick={() => onReact(emoji)}
          data-testid={`reaction-${emoji}`}
          className={`inline-flex items-center space-x-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-all hover:scale-110 ${
            users.includes(currentUserId)
              ? 'bg-[#4A90E2]/10 border-[#4A90E2]/30'
              : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
          }`}
        >
          <span>{emoji}</span>
          {users.length > 1 && <span className="text-[10px] text-gray-500 font-medium">{users.length}</span>}
        </button>
      ))}
    </div>
  );
};

const ReactionPicker = ({ show, onSelect, isMine }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 4 }}
        transition={{ duration: 0.15 }}
        className={`absolute ${isMine ? 'right-0' : 'left-0'} -top-10 z-20 flex items-center space-x-0.5 bg-white rounded-full shadow-lg border border-gray-200 px-2 py-1`}
        data-testid="reaction-picker"
      >
        {REACTION_EMOJIS.map(emoji => (
          <button
            key={emoji}
            onClick={(e) => { e.stopPropagation(); onSelect(emoji); }}
            data-testid={`pick-reaction-${emoji}`}
            className="text-lg hover:scale-125 transition-transform p-0.5"
          >
            {emoji}
          </button>
        ))}
      </motion.div>
    )}
  </AnimatePresence>
);

const MessageBubble = ({ msg, currentUserId, selectedConversation, onReact }) => {
  const [showPicker, setShowPicker] = useState(false);
  const isMine = msg.sender_id === currentUserId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-0.5 group`}
      data-testid={`message-${msg.id}`}
      onMouseLeave={() => setShowPicker(false)}
    >
      <div className={`max-w-[70%] relative ${isMine ? 'order-2' : ''}`}>
        {selectedConversation.type === 'group' && !isMine && (
          <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
        )}

        <div className="relative">
          {/* Reaction picker trigger - appears on hover */}
          <button
            onClick={() => setShowPicker(p => !p)}
            data-testid={`reaction-trigger-${msg.id}`}
            className={`absolute ${isMine ? '-left-8' : '-right-8'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600`}
          >
            <FaSmile size={14} />
          </button>

          <ReactionPicker
            show={showPicker}
            isMine={isMine}
            onSelect={(emoji) => { onReact(msg.id, emoji); setShowPicker(false); }}
          />

          <div
            className={`rounded-2xl px-4 py-2 ${
              isMine
                ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white'
                : 'bg-white text-gray-800 border border-gray-200'
            } ${msg.status === 'sending' ? 'opacity-70' : ''}`}
          >
            <p className="text-sm">{msg.content}</p>
          </div>
        </div>

        <ReactionBubbles
          reactions={msg.reactions}
          currentUserId={currentUserId}
          onReact={(emoji) => onReact(msg.id, emoji)}
        />

        <div className={`flex items-center space-x-1 mt-0.5 text-[10px] text-gray-400 ${isMine ? 'justify-end' : ''}`}>
          <span>{formatMsgTime(msg.created_at)}</span>
          {isMine && (
            msg.status === 'sending' ? (
              <span className="text-gray-300">sending</span>
            ) : msg.is_read ? (
              <FaCheckDouble size={8} className="text-[#4A90E2]" />
            ) : (
              <FaCheck size={8} className="text-gray-400" />
            )
          )}
        </div>
      </div>
    </motion.div>
  );
};

const formatLastSeen = (lastSeenStr) => {
  if (!lastSeenStr) return 'Offline';
  const diff = Date.now() - new Date(lastSeenStr).getTime();
  if (diff < 60000) return 'Last seen just now';
  if (diff < 3600000) return `Last seen ${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `Last seen ${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `Last seen ${Math.floor(diff / 86400000)}d ago`;
  return `Last seen ${new Date(lastSeenStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const ChatWindow = ({
  selectedConversation, messages, newMessage, setNewMessage,
  onSendMessage, onBack, currentUserId, messagesEndRef, formatTime,
  onNewChat, onlineUsers, isTyping, onTyping, connected, onReact
}) => {
  const { initiateCall, callState } = useCall();

  if (!selectedConversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500" data-testid="no-conversation-selected">
        <div className="w-20 h-20 bg-gradient-to-br from-[#4A90E2] to-[#A855F7] rounded-2xl flex items-center justify-center mb-4">
          <span className="text-white text-3xl font-bold">T</span>
        </div>
        <p className="font-medium text-lg">Real-Time Messages</p>
        <p className="text-sm text-gray-400 mt-1">Send and receive messages instantly</p>
        {connected && (
          <span className="flex items-center space-x-1 text-xs text-green-500 mt-2">
            <FaCircle size={6} />
            <span>Connected</span>
          </span>
        )}
        <button
          onClick={onNewChat}
          className="mt-4 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white px-6 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
          data-testid="start-conversation-btn"
        >
          Start a conversation
        </button>
      </div>
    );
  }

  const isOnline = onlineUsers?.has(selectedConversation.id);
  const isDirect = selectedConversation.type !== 'group';

  const handleVoiceCall = () => {
    if (callState !== 'idle') return;
    initiateCall(
      selectedConversation.id,
      selectedConversation.name,
      selectedConversation.avatar_url,
      selectedConversation.username,
      'voice'
    );
  };

  const handleVideoCall = () => {
    if (callState !== 'idle') return;
    initiateCall(
      selectedConversation.id,
      selectedConversation.name,
      selectedConversation.avatar_url,
      selectedConversation.username,
      'video'
    );
  };

  return (
    <>
      {/* Chat Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-testid="chat-header">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="md:hidden text-gray-600" data-testid="chat-back-btn">
            <FaArrowLeft />
          </button>
          <div className="relative">
            {selectedConversation.type === 'group' ? (
              <div className="w-10 h-10 bg-gradient-to-br from-[#4A90E2] to-[#A855F7] rounded-full flex items-center justify-center">
                <FaUsers className="text-white" />
              </div>
            ) : (
              <img
                src={selectedConversation.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'}
                alt={selectedConversation.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            )}
            {isOnline && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" data-testid="online-indicator" />
            )}
          </div>
          <div>
            <p className="text-gray-900 font-medium" data-testid="chat-user-name">{selectedConversation.name}</p>
            <p className="text-xs" data-testid="chat-status">
              {isTyping ? (
                <span className="text-[#4A90E2] font-medium">typing...</span>
              ) : isOnline && selectedConversation.show_online_status !== false ? (
                <span className="text-green-500">Online</span>
              ) : selectedConversation.show_last_seen !== false && selectedConversation.last_seen ? (
                <span className="text-gray-400">{formatLastSeen(selectedConversation.last_seen)}</span>
              ) : (
                <span className="text-gray-400">Offline</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          {isDirect && (
            <>
              <button
                onClick={handleVoiceCall}
                disabled={callState !== 'idle'}
                className="p-2 text-gray-600 hover:text-[#4A90E2] hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="voice-call-btn"
                title="Voice call"
              >
                <FaPhone size={16} />
              </button>
              <button
                onClick={handleVideoCall}
                disabled={callState !== 'idle'}
                className="p-2 text-gray-600 hover:text-[#4A90E2] hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="video-call-btn"
                title="Video call"
              >
                <FaVideo size={16} />
              </button>
            </>
          )}
          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
            <FaEllipsisV />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-gray-50" data-testid="messages-container">
        {messages.map((msg, index) => {
          const prevMsg = index > 0 ? messages[index - 1] : null;
          const showDate = shouldShowDateSeparator(msg, prevMsg);
          const showTime = !showDate && shouldShowTimeSeparator(msg, prevMsg);

          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <div className="flex items-center justify-center py-3" data-testid="date-separator">
                  <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full font-medium">
                    {getDateLabel(msg.created_at)}
                  </span>
                </div>
              )}
              {showTime && (
                <div className="flex items-center justify-center py-1.5" data-testid="time-separator">
                  <span className="text-[10px] text-gray-400">{formatMsgTime(msg.created_at)}</span>
                </div>
              )}
              <MessageBubble
                msg={msg}
                currentUserId={currentUserId}
                selectedConversation={selectedConversation}
                onReact={onReact}
              />
            </React.Fragment>
          );
        })}

        {isTyping && (
          <div className="flex justify-start mb-1">
            <TypingIndicator />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <form onSubmit={onSendMessage} className="flex items-center space-x-2">
          <button type="button" className="p-2 text-gray-500 hover:text-[#4A90E2] transition-colors">
            <FaImage size={18} />
          </button>
          <button type="button" className="p-2 text-gray-500 hover:text-[#4A90E2] transition-colors">
            <FaSmile size={18} />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => { setNewMessage(e.target.value); onTyping?.(); }}
            placeholder="Type a message..."
            className="flex-1 bg-gray-100 border-none rounded-full px-4 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/50"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white rounded-full p-3 transition-all hover:opacity-90 disabled:opacity-50"
            data-testid="send-message-btn"
          >
            <FaPaperPlane size={14} />
          </button>
        </form>
      </div>
    </>
  );
};

export default ChatWindow;
